/* eslint-disable max-lines */
import React, { memo, useCallback, useEffect, useState } from 'react';
import cn from 'clsx';
import { Modal } from 'fronton-react';
import { analyticsUtils, ClickButtonElementName } from 'utils/analytics-utils';
import { createElementKey } from 'utils';
import { EffectFade } from 'swiper/modules';
import { ImageName } from '../image-name';
import { getImageSrc, Picture } from '../picture';
import { FreeMode, Thumbs, Swiper, SwiperWithNavigation, SwiperClass, SwiperSlide } from '../swiper';
import {
  activeImagePreview,
  footerButton,
  imagePreview,
  modalStyles,
  modalContent,
  modalFooter,
  modalImage,
  modalImageContainer,
  swiperImage,
  imageWrapper,
  swiperPreview,
  swiperSlide,
} from './gallery-modal-styles';
import { GALLERY_IMAGE_TRANSFORMATIONS } from './gallsery-modal-constants';

type GalleryModalProps = {
  clickedImageIndex: number;
  elementBlock: string;
  elementText: string;
  galleryList: GalleryModalImage[];
  onClose: () => void;
  show: boolean;
  isShowImageName?: boolean;
  isShowImageAddText?: boolean;
  renderFooterButton?: () => React.ReactElement;
  newAnalytics?: boolean,
};

export type GalleryModalImage = {
  alt: string;
  src: string;
  srcThumb?: string;
  name: string;
  addText?: string;
} & Record<string, unknown>;

/**
 * Компонент модального окна для просмотра картинок галереии
 */
export const GalleryModal: React.FC<GalleryModalProps> = memo(
  ({
    clickedImageIndex,
    elementBlock,
    elementText,
    show,
    onClose,
    galleryList,
    renderFooterButton,
    isShowImageName,
    isShowImageAddText,
    newAnalytics,
  }) => {
    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null);
    const [isThumbClicked, setIsThumbClicked] = useState<boolean>(false);
    const [activeImageIndex, setActiveImageIndex] = useState<number>(clickedImageIndex);
    const mainImageList = galleryList.map((img, index) => {
      if (index === activeImageIndex) {
        return img;
      }
      return null;
    });

    useEffect(() => {
      if (!show) {
        setActiveImageIndex(0);
        setThumbsSwiper(null);
      } else {
        analyticsUtils.showEvent(elementBlock, 'Модальное окно', elementText);
      }
    }, [setThumbsSwiper, show, elementBlock, elementText]);

    /**
     * Обработчик нажатия кнопки "Следующий слайд"
     */
    const handleNextSlideBtnClick = useCallback(() => {
      if (!thumbsSwiper || isThumbClicked) {
        setIsThumbClicked(false);
        return;
      }

      analyticsUtils.clickButtonEvent(elementBlock, ClickButtonElementName.SwitchForward, elementText);
    }, [elementBlock, thumbsSwiper, isThumbClicked, elementText]);

    /**
     * Обработчик нажатия кнопки "Предыдущий слайд"
     */
    const handlePrevSlideBtnClick = useCallback(() => {
      if (!thumbsSwiper || isThumbClicked) {
        setIsThumbClicked(false);
        return;
      }

      analyticsUtils.clickButtonEvent(elementBlock, ClickButtonElementName.SwitchBackward, elementText);
    }, [elementBlock, thumbsSwiper, isThumbClicked, elementText]);

    /**
     * Обработчик клика по превью картинки в модально окне
     */
    const handlePreviewImageClick = useCallback(
      (index: number) => () => {
        setIsThumbClicked(true);
        if (newAnalytics) {
          analyticsUtils.clickButtonEvent(elementBlock, elementText, galleryList[index].name);
        } else {
          analyticsUtils.clickButtonEvent(elementBlock, galleryList[index].name, elementText);
        }
      },
      [elementBlock, galleryList, elementText, newAnalytics]
    );

    /**
     * Обработчик смены слайда
     */
    const handleSlideChange = useCallback(
      ({ previousIndex, activeIndex }: SwiperClass) => {
        setActiveImageIndex(activeIndex);
        if (activeIndex > previousIndex) {
          handleNextSlideBtnClick();
        } else if (activeIndex < previousIndex) {
          handlePrevSlideBtnClick();
        }
      },
      [setActiveImageIndex, handleNextSlideBtnClick, handlePrevSlideBtnClick]
    );

    return (
      <Modal onClose={onClose} show={show} className={modalStyles}>
        <div className={modalContent}>
          <div className={modalImageContainer}>
            <SwiperWithNavigation
              className={swiperImage}
              initialSlide={clickedImageIndex}
              modules={[EffectFade, FreeMode, Thumbs]}
              onSlideChange={handleSlideChange}
              thumbs={{
                autoScrollOffset: 2,
                swiper: thumbsSwiper,
              }}
              effect="fade">
              {mainImageList.map((image, index: number) => (
                <SwiperSlide key={createElementKey('imagePreview', index)}>
                  <div className={imageWrapper}>
                    {image && (
                      <Picture
                        alt={image.alt}
                        className={modalImage}
                        src={image.src}
                        title={image.alt}
                        transformations={GALLERY_IMAGE_TRANSFORMATIONS}
                      />
                    )}
                    {image && isShowImageName && (
                      <ImageName
                        isShowImageAddText={isShowImageAddText}
                        addText={image.addText}
                        name={image.alt}
                      />
                    )}
                  </div>
                </SwiperSlide>
              ))}
            </SwiperWithNavigation>
          </div>
          <div className={modalFooter}>
            <Swiper
              freeMode
              initialSlide={clickedImageIndex}
              modules={[FreeMode, Thumbs]}
              onSwiper={setThumbsSwiper}
              className={swiperPreview}
              centeredSlides
              centeredSlidesBounds
              slidesPerView="auto"
              watchOverflow
            >
              {galleryList.map(({ src, srcThumb, alt }, index: number) => (
                <SwiperSlide
                  className={cn(swiperSlide, { [activeImagePreview]: activeImageIndex === index })}
                  onClick={handlePreviewImageClick(index)}
                  key={createElementKey('imagePreview', index)}>
                  <img
                    alt={alt}
                    className={imagePreview}
                    height={32}
                    key={createElementKey('imagePreview', index)}
                    src={srcThumb || getImageSrc(src, { width: 32, height: 32, objectFit: 'fill' }, true)}
                    title={alt}
                    width={32}
                    loading="lazy"
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            {renderFooterButton && <div className={footerButton}>{renderFooterButton()}</div>}
          </div>
        </div>
      </Modal>
    );
  }
);
